// 메인 레이아웃 - 전체 앱 구조
import React from 'react';
import { changedFields } from '../../utils/changedFields';
import { Layout, Typography, Space, Button } from 'antd';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import Sidebar from './Sidebar';
import ContentArea from './ContentArea';
import UnifiedModal from '../common/UnifiedModal';
import CopyModal from '../common/CopyModal';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { skillApi, effectApi, bossApi, improvedTriggerApi } from '../../services/api';
import { BossFormData, SkillFormData, EffectFormData, TriggerFormData } from '../../types';
import { message } from 'antd';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const MainLayout: React.FC = () => {
  const { state, closeAllModals } = useApp();
  const [notice,noticeHolder]=message.useMessage();
  const { user, logout } = useAuth();
  const { loading: referenceLoading, error: referenceError } = useReferenceData();

  // 모달 저장 핸들러들
  const handleBossSave = async (data: BossFormData) => {
    try {
      if (state.editingMode === 'edit' && state.editingItem && 'boss_code' in state.editingItem) {
        await bossApi.update(state.editingItem.boss_code, data);
        notice.success('보스가 수정되었습니다.');
      } else {
        await bossApi.create({
          ...data,
          id: 0 // auto-generated by database
        });
        notice.success('새 보스가 생성되었습니다.');
      }
      closeAllModals();
      window.dispatchEvent(new Event('raidDataUpdated'));
      window.dispatchEvent(new Event('skillsUpdated'));
    } catch (error) {
      console.error('보스 저장 실패:', error);
      notice.error('저장에 실패했습니다. 입력 내용은 유지됩니다.');
      throw error;
    }
  };

  const handleSkillSave = async (data: SkillFormData) => {
    try {
      // 경고 상세 설정이 객체인 경우 JSON 문자열로 변환
      if (data.warning_detail && typeof data.warning_detail === 'object') {
        data.warning_detail = JSON.stringify(data.warning_detail);
      }

      const skillData = { ...data };

      if (state.editingMode === 'edit' && state.editingItem && 'key' in state.editingItem) {
        const patch=changedFields(state.editingItem,skillData);
        if(Object.keys(patch).length) await skillApi.update(state.editingItem.key, patch);
        notice.success('스킬이 수정되었습니다.');
      } else {
        await skillApi.create({...skillData,game_version:skillData.game_version || 'v1.0'});
        notice.success('새 스킬이 생성되었습니다.');
      }
      closeAllModals();
      window.dispatchEvent(new Event('raidDataUpdated'));
      window.dispatchEvent(new Event('skillsUpdated'));
    } catch (error) {
      console.error('스킬 저장 실패:', error);
      notice.error('저장에 실패했습니다. 입력 내용은 유지됩니다.');
      throw error;
    }
  };

  const handleEffectSave = async (data: EffectFormData) => {
    try {
      
      // special_params는 JSONB로 직접 전달
      // id, created_at, updated_at 같은 읽기 전용 필드 제거
      const { id, created_at, updated_at, ...cleanData } = data as any;
      const effectData = { ...cleanData };
      
      
      if (state.editingMode === 'edit' && state.editingItem && 'key' in state.editingItem) {
          console.log('📤 MainLayout에서 전송하는 effectData:', effectData);
          console.log('📤 editingItem:', state.editingItem);
          const patch=changedFields(state.editingItem,effectData);
          if(Object.keys(patch).length) await effectApi.update(state.editingItem.key, patch);
          notice.success('효과가 수정되었습니다.');
      } else {
          await effectApi.create({...effectData,game_version:effectData.game_version || 'v1.0'});
          notice.success('새 효과가 생성되었습니다.');
      }
      closeAllModals();
      
      // 효과 목록 새로고침 이벤트 발생
      window.dispatchEvent(new CustomEvent('effectsUpdated'));
    } catch (error) {
      console.error('❌ 효과 저장 실패:', error);
      console.error('❌ 에러 상세:', JSON.stringify(error, null, 2));
      notice.error('저장에 실패했습니다. 입력 내용은 유지됩니다.');
      throw error;
    }
  };

  const handleTriggerSave = async (data: TriggerFormData) => {
    try {
      const triggerData = {
        ...data,
        game_version: 'v1.0',
        updated_at: new Date().toISOString()
      };

      if (state.editingMode === 'edit' && state.editingItem && 'id' in state.editingItem) {
        // Legacy trigger update - no longer supported
        notice.error('구버전 트리거 시스템은 더 이상 지원되지 않습니다.');
        notice.success('트리거가 수정되었습니다.');
      } else {
        // Legacy trigger create - no longer supported
        notice.error('구버전 트리거 시스템은 더 이상 지원되지 않습니다.');
        notice.success('새 트리거가 생성되었습니다.');
      }
      closeAllModals();
      window.dispatchEvent(new Event('raidDataUpdated'));
      window.dispatchEvent(new Event('skillsUpdated'));
    } catch (error) {
      console.error('트리거 저장 실패:', error);
      notice.error('저장에 실패했습니다. 입력 내용은 유지됩니다.');
      throw error;
    }
  };

  const handleCopySuccess = () => {
    window.dispatchEvent(new Event('raidDataUpdated'));
      window.dispatchEvent(new Event('skillsUpdated'));
    notice.success('복사가 완료되었습니다.');
  };

  // 참조 데이터 로딩 중
  if (referenceLoading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Space direction="vertical" align="center">
          <Title level={3}>레이드 보스 관리 시스템</Title>
          <p>참조 데이터를 로드하는 중입니다...</p>
        </Space>
      </div>
    );
  }

  // 참조 데이터 로드 실패
  if (referenceError) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Space direction="vertical" align="center">
          <Title level={3}>오류 발생</Title>
          <p style={{ color: 'red' }}>{referenceError}</p>
          <Button onClick={() => window.location.reload()}>
            페이지 새로고침
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#0a0a0a' }}>
      {noticeHolder}
      {/* 사이드바 */}
      <Sidebar />

      {/* 메인 콘텐츠 영역 */}
      <Layout style={{ marginLeft: state.sidebarCollapsed ? 80 : 250 }}>
        {/* 헤더 */}
        <Header style={{ 
          background: '#141414', 
          padding: '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <Title level={3} style={{ margin: 0 }}>
            {state.viewingCommon ? (
              `공통 ${state.commonResourceType === 'skills' ? '스킬' : '효과'}`
            ) : state.selectedBoss ? (
              <>
                {state.selectedBoss.boss_name}
                <span style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  marginLeft: '8px' 
                }}>
                  ({state.selectedBoss.boss_code})
                </span>
              </>
            ) : state.selectedEntity ? (
              <>
                {state.selectedEntity.entity_name}
                <span style={{ 
                  fontSize: '14px', 
                  color: '#666', 
                  marginLeft: '8px' 
                }}>
                  ({state.selectedEntity.entity_type} - {state.selectedEntity.entity_code})
                </span>
              </>
            ) : state.activeTab === 'raid-management' ? (
              '레이드 구성 관리'
            ) : state.activeTab === 'drop-item-management' ? (
              '드롭템 관리'
            ) : (
              '보스나 엔티티를 선택하세요'
            )}
          </Title>

          <Space>
            {user && (
              <>
                <span style={{ color: 'white' }}>
                  <UserOutlined /> {user.name || user.email}
                </span>
                <Button 
                  type="primary" 
                  danger 
                  icon={<LogoutOutlined />}
                  onClick={logout}
                >
                  로그아웃
                </Button>
              </>
            )}
            <Text type="secondary">변경 사항은 각 편집창에서 저장합니다.</Text>
          </Space>
        </Header>

        {/* 콘텐츠 영역 */}
        <Content style={{ 
          margin: '24px',
          background: '#141414',
          borderRadius: '8px',
          overflow: 'auto'
        }}>
          <ContentArea />
        </Content>
      </Layout>

      {/* 모달들 */}
      {state.showBossModal && (
        <UnifiedModal
          visible={state.showBossModal}
          onCancel={closeAllModals}
          onOk={(data) => handleBossSave(data as BossFormData)}
          modalType="boss"
          editingItem={state.editingItem}
          mode={state.editingMode || 'create'}
        />
      )}

      {state.showSkillModal && (
        <UnifiedModal
          visible={state.showSkillModal}
          onCancel={closeAllModals}
          onOk={(data) => handleSkillSave(data as SkillFormData)}
          modalType="skill"
          editingItem={state.editingItem}
          mode={state.editingMode || 'create'}
          currentBossCode={state.viewingCommon ? 'COMMON' : state.selectedEntity?.entity_code || state.selectedBoss?.boss_code}
        />
      )}

      {state.showEffectModal && (
        <UnifiedModal
          visible={state.showEffectModal}
          onCancel={closeAllModals}
          onOk={(data) => handleEffectSave(data as EffectFormData)}
          modalType="effect"
          editingItem={state.editingItem}
          mode={state.editingMode || 'create'}
          currentBossCode={state.viewingCommon ? 'COMMON' : state.selectedEntity?.entity_code || state.selectedBoss?.boss_code}
        />
      )}

      {state.showTriggerModal && (
        <UnifiedModal
          visible={state.showTriggerModal}
          onCancel={closeAllModals}
          onOk={(data) => handleTriggerSave(data as TriggerFormData)}
          modalType="trigger"
          editingItem={state.editingItem}
          mode={state.editingMode || 'create'}
          currentBossCode={state.viewingCommon ? 'COMMON' : state.selectedEntity?.entity_code || state.selectedBoss?.boss_code}
        />
      )}

      {state.showCopyModal && (
        <CopyModal
          visible={state.showCopyModal}
          onCancel={closeAllModals}
          onSuccess={handleCopySuccess}
        />
      )}
    </Layout>
  );
};

export default MainLayout;